/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */

/**
 * Provides simple conversion from miles to kilometers.
 *
 * @function milesToKilometers
 * @param  {number} val The miles to be converted to kilometers
 */
export const milesToKilometers = (val: number): number => {
  return val ? Math.round((val * 1.60934) * 10) / 10 : val;
};

/**
 * Provides simple conversion from feet to centimeters
 *
 * @function feetToCentimeters
 * @param  {number} val The feet to be converted to centimeters
 */
export const feetToCentimeters = (val: number): number => {
  return val ? Math.round((val * 30.48) * 10) / 10 : val;
};

/**
 * Provides simple conversion from feet to meters
 *
 * @function feetToMeters
 * @param  {number} val The feet to be converted to meters
 */
export const feetToMeters = (val: number): number => {
  return val ? Math.round((val * 0.3048) * 10) / 10 : val;
};

/**
 * Provides simple conversion from centimeters to feet
 *
 * @function centimetersToFeet
 * @param  {number} val The centimeters to be converted to feet
 */
export const centimetersToFeet = (val: number): number => {
  return val ? Math.round((val * 0.0328084) * 10) / 10 : val;
};

/**
 * Provides simple conversion from meters to feet.
 *
 * @function metersToFeet
 * @param  {number} val The meters to be converted to feet
 */
export const metersToFeet = (val: number): number => {
  return val ? Math.round((val * 3.28084) * 10) / 10 : val;
};

/**
 * Provides simple conversion from meters to kilometers.
 *
 * @function metersToKilometers
 * @param  {number} val The meters to be converted to kilometers
 */
export const metersToKilometers = (val: number): number => {
  return val ? Math.round((val * 0.001) * 10) / 10 : val;
};

/**
 * Provides simple conversion from miles to meters.
 *
 * @function milesToMeters
 * @param  {number} val The miles to be converted to meters
 */
export const milesToMeters = (val: number): number => {
  return val ? Math.round((val * 1609.34) * 10) / 10 : val;
};

/**
 * Provides simple conversion from meters to miles.
 *
 * @function metersToMiles
 * @param  {number} val The meters to be converted to miles
 */
export const metersToMiles = (val: number): number => {
  return val ? Math.round((val * 0.000621371) * 10) / 10 : val;
};

/**
 * Provides simple conversion from kilometers to miles.
 *
 * @function kilometersToMiles
 * @param  {number} val The kilometers to be converted to miles
 */
export const kilometersToMiles = (val: number): number => {
  return val ? Math.round((val * 0.621371) * 10) / 10 : val;
};

/**
 * Provides simple conversion from kilometers to meters.
 *
 * @function kilometersToMeters
 * @param  {number} val The kilometers to be converted to meters
 */
export const kilometersToMeters = (val: number): number => {
  return val ? Math.round(val * 1000) : val;
};

/**
 * Provides simple conversion from miles per hour to kilometers per hour.
 *
 * @function milesPerHourToKilometersPerHour
 * @param  {number} val The miles per hour
 */
export const milesPerHourToKilometersPerHour = (val: number): number => {
  return miToKm(val);
};

/**
 * Provides simple conversion from miles per hour to meters per hour.
 *
 * @function milesPerHourToMetersPerHour
 * @param  {number} val The miles per hour
 */
export const milesPerHourToMetersPerHour = (val: number): number => {
  return miToM(val);
};

/**
 * Provides simple conversion from miles per hour to meters per second.
 *
 * @function milesPerHourToMetersPerSecond
 * @param  {number} val The miles per hour
 */
export const milesPerHourToMetersPerSecond = (val: number): number => {
  return val ? Math.round((val * 0.44704) * 10) / 10 : val;
};

/**
 * Provides simple conversion from miles per hour to centimeters per second.
 *
 * @function milesPerHourToCentimetersPerSecond
 * @param  {number} val The miles per hour
 */
export const milesPerHourToCentimetersPerSecond = (val: number): number => {
  return val ? Math.round((val * 44.704) * 10) / 10 : val;
};

/**
 * Provides simple conversion from kilometers per hour to meters per hour
 *
 * @function kilometersPerHourToMetersPerHour
 * @param  {number} val The kilometers per hour
 */
export const kilometersPerHourToMetersPerHour = (val: number): number => {
  return val ? mToMi(val * 1000) : val;
};

/**
 * Provides simple conversion from centimeters per second to miles per hour.
 *
 * @function centimetersPerSecondToMilesPerHour
 * @param  {number} val The centimeters per second.
 */
export const centimetersPerSecondToMilesPerHour = (val: number): number => {
  return val ? Math.round((val * 0.0223694) * 10) / 10 : val;
};

/**
 * Provides simple conversion from centimeters per second to meters per hour.
 *
 * @function centimetersPerSecondToMetersPerHour
 * @param  {number} val The centimeters per second.
 */
export const centimetersPerSecondToMetersPerHour = (val: number): number => {
  return val ? Math.round((val * 36) * 10) / 10 : val;
};

/**
 * Provides simple conversion from centimeters per second to kilometers per hour.
 *
 * @function centimetersPerSecondToKilometersPerHour
 * @param  {number} val The centimeters per second.
 */
export const centimetersPerSecondToKilometersPerHour = (val: number): number => {
  return val ? Math.round((val * 0.036) * 10) / 10 : val;
};

/**
 * Provides simple conversion from meters per second to kilometers per hour.
 *
 * @function metersPerSecondToKilometersPerHour
 * @param  {number} val The meters per second.
 */
export const metersPerSecondToKilometersPerHour = (val: number): number => {
  return val ? Math.round((val * 3.6) * 10) / 10 : val;
};

/**
 * Provides simple conversion from centimeters per second to meters per second.
 *
 * @function centimetersPerSecondToMetersPerSecond
 * @param  {number} val The centimeters per second.
 */
export const centimetersPerSecondToMetersPerSecond = (val: number): number => {
  return val ? Math.round((val * 0.01) * 10) / 10 : val;
};

/**
 * Provides simple conversion from kilometers per hour to meters per second.
 *
 * @function kilometersPerHourToMetersPerSecond
 * @param  {number} val The kilometers per hour.
 */
export const kilometersPerHourToMetersPerSecond = (val: number): number => {
  return val ? Math.round((val * 0.277778) * 10) / 10 : val;
};

/**
 * Provides simple conversion from kilometers per hour to miles per hour.
 *
 * @function kilometersPerHourToMilesPerHour
 * @param  {number} val The kilometers per hour.
 */
export const kilometersPerHourToMilesPerHour = (val: number): number => {
  return kmToMi(val);
};

/**
 * Provides simple conversion from kilometers per hour to centimeters per second.
 *
 * @function kilometersPerHourToCentimetersPerSecond
 * @param  {number} val The kilometers per hour.
 */
export const kilometersPerHourToCentimetersPerSecond = (val: number): number => {
  return val ? Math.round((val * 27.7778) * 10) / 10 : val;
};

/**
 * Provides simple conversion from meters per second to miles per hour.
 *
 * @function metersPerSecondToMilesPerHour
 * @param  {number} val The meters per second.
 */
export const metersPerSecondToMilesPerHour = (val: number): number => {
  return val ? Math.round((val * 2.23694) * 10) / 10 : val;
};

/**
 * Provides a simple conversion from pounds to kilograms.
 * 
 * @function poundsToKilograms
 * @returns {number} val The pounds to kilograms.
 */
export const poundsToKilograms = (val: number): number => {
  return val ? Math.round((val * 0.453592) * 10) / 10 : val;
};

/**
 * Provides a simple conversion from kilograms to pounds.
 * 
 * @function kilogramsToPounds
 * @returns {number} val The kilograms to pounds.
 */
export const kilogramsToPounds = (val: number): number => {
  return val ? Math.round((val * 2.20462) * 10) / 10 : val;
};

/**
 * Provides a simple conversion from liters to gallons.
 * 
 * @returns {number} val The liters to gallons.
 */
export const litersToGallons = (val: number): number => {
  return val ? Math.round((val * 0.264172) * 10) / 10 : val;
};

/**
 * Provides a simple conversion from gallons to liters.
 * 
 * @returns {number} val The gallons to liters.
 */
export const gallonsToLiters = (val: number): number => {
  return val ? Math.round((val * 3.78541) * 10) / 10 : val;
};

/**
 * Provides a simple conversion from inches to centimeters.
 * 
 * @returns {number} val The gallons to liters.
 */
export const inchesToCm = (val: number): number => {
  return val ? Math.round((val * 2.54) * 10) / 10 : val;
};

/**
 * Provides a simple conversion from centimeters to inches.
 * 
 * @returns {number} val The gallons to liters.
 */
export const cmToInches = (val: number): number => {
  return val ? Math.round((val * 0.393701) * 10) / 10 : val;
};

// short-hand references ...
export const miToKm     = (val: number): number => milesToKilometers(val);
export const ftToCm     = (val: number): number => feetToCentimeters(val);
export const ftToM      = (val: number): number => feetToMeters(val);
export const cmToFt     = (val: number): number => centimetersToFeet(val);
export const mToFt      = (val: number): number => metersToFeet(val);
export const mToKm      = (val: number): number => metersToKilometers(val);
export const miToM      = (val: number): number => milesToMeters(val);
export const mToMi      = (val: number): number => metersToMiles(val);
export const kmToMi     = (val: number): number => kilometersToMiles(val);
export const miphToKmph = (val: number): number => milesPerHourToKilometersPerHour(val);
export const miphToMph  = (val: number): number => milesPerHourToMetersPerHour(val);
export const miphToMps  = (val: number): number => milesPerHourToMetersPerSecond(val);
export const miphToCmps = (val: number): number => milesPerHourToCentimetersPerSecond(val);
export const cmpsToMiph = (val: number): number => centimetersPerSecondToMilesPerHour(val);
export const cmpsToKmph = (val: number): number => centimetersPerSecondToKilometersPerHour(val);
export const cmpsToMph  = (val: number): number => centimetersPerSecondToMetersPerHour(val);
export const cmpsToMps  = (val: number): number => centimetersPerSecondToMetersPerSecond(val);
export const mpsToMiph  = (val: number): number => metersPerSecondToMilesPerHour(val);
export const mpsToKmph  = (val: number): number => metersPerSecondToKilometersPerHour(val);
export const kmphToMps  = (val: number): number => kilometersPerHourToMetersPerSecond(val);
export const kmphToMph  = (val: number): number => kilometersPerHourToMetersPerHour(val);
export const kmphToMiph = (val: number): number => kilometersPerHourToMilesPerHour(val);
export const kmphToCmps = (val: number): number => kilometersPerHourToCentimetersPerSecond(val);
export const lbsToKg    = (val: number): number => poundsToKilograms(val);
export const kgToLbs    = (val: number): number => kilogramsToPounds(val);
